import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthState, useSignInWithGoogle } from "react-firebase-hooks/auth";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import auth from "../firebase.init";
import Loading from "../components/Shared/Loading";

const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "auth/popup-closed-by-user":
      return "Sign-in popup closed by user.";
    case "auth/popup-blocked":
      return "Sign-in popup was blocked. Please allow popups for this site.";
    case "auth/cancelled-popup-request":
      return "Sign-in cancelled. Please try again.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/email-already-in-use":
      return "This email is already in use. Please try another one.";
    case "auth/invalid-email":
      return "Invalid email address format.";
    case "auth/weak-password":
      return "Password is too weak. Please use a stronger password.";
    case "auth/operation-not-allowed":
      return "Email/password registration is not enabled.";
    default:
      return "An error occurred during registration. Please try again.";
  }
};

const Register = () => {
  const [signInWithGoogle, gUser, gLoading, gError] = useSignInWithGoogle(auth);
  const [apiError, setApiError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailPasswordError, setEmailPasswordError] = useState(null);
  const [emailPasswordLoading, setEmailPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const [userMail] = useAuthState(auth);

  useEffect(() => {
    if (userMail) {
      navigate("/user/dashboard", { replace: true });
    }
  }, [userMail, navigate]);

  let from = location.state?.from?.pathname || "/user/dashboard";
  let signUpError;

  useEffect(() => {
    if (gError || apiError || emailPasswordError) {
      window.scrollTo(0, 0);
    }
  }, [gError, apiError, emailPasswordError]);

  // Create user in database after successful Firebase authentication
  const createUserInDb = async (name, email, photoURL) => {
    try {
      const response = await fetch(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/create-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userName: name,
            userEmail: email,
            profileURL: photoURL || null,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create user");
      }
      console.log("User created with credits:", data);
      return data;
    } catch (err) {
      console.error("Error creating user in database:", err);
      setApiError(err.message);
      return null;
    }
  };

  useEffect(() => {
    const handleUserCreation = async () => {
      if (gUser) {
        setIsProcessing(true);
        const { displayName, email, photoURL } = auth.currentUser;
        await createUserInDb(displayName, email, photoURL);
        setIsProcessing(false);
        navigate(from, { replace: true });
      }
    };
    handleUserCreation();
  }, [gUser, from, navigate]);

  const handleGoogleSignIn = () => {
    setApiError(null);
    setEmailPasswordError(null);
    signInWithGoogle();
  };

  const validatePassword = () => {
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleEmailPasswordRegister = async (e) => {
    e.preventDefault();
    setEmailPasswordError(null);

    if (!validatePassword()) {
      return;
    }

    setEmailPasswordLoading(true);

    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Update user profile with name
      await updateProfile(result.user, {
        displayName: name,
      });

      // Create user in database
      setIsProcessing(true);
      await createUserInDb(name, email, result.user.photoURL);
      setIsProcessing(false);

      navigate(from, { replace: true });
    } catch (error) {
      setEmailPasswordError(getErrorMessage(error.code));
      console.error("Email/password registration error:", error);
    } finally {
      setEmailPasswordLoading(false);
    }
  };

  if (gLoading || isProcessing || emailPasswordLoading) {
    return <Loading />;
  }

  if (gError || apiError || emailPasswordError) {
    const errorCode = gError?.code;
    signUpError = (
      <div className="error-message">
        <i className="fas fa-exclamation-circle"></i>
        <p>{apiError || emailPasswordError || getErrorMessage(errorCode)}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Register - Join Our Digital Marketplace</title>
        <meta
          name="description"
          content="Discover proven strategies and tools to instantly find high-quality SEO clients. Learn how to attract, pitch, and close deals faster to grow your digital marketing business."
        />
      </Helmet>
      <section className="modern-register-page body-bg" style={{ marginTop: "0px" }}>
        <div className="register-container mt-5">
          <div className="register-card">
            <div className="register-header">
              <div className="close-button">
                <Link to="/" className="close-link">
                  <i className="fas fa-times"></i>
                </Link>
              </div>

              <div className="logo-section">
                {/* <Link to="/">
                  <img
                    src="images/resource/login-page-logo.png"
                    alt="Logo"
                    className="logo"
                  />
                </Link> */}
              </div>

              <div className="welcome-section">
                <h1>Welcome to Our Platform</h1>
                <p>Join thousands of users and start your journey with us</p>
              </div>
            </div>
            <div className="register-content">
              {signUpError}

              {/* Email/Password Registration Form */}
              <form
                onSubmit={handleEmailPasswordRegister}
                className="email-password-form"
              >
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Create a password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm your password"
                  />
                  {passwordError && (
                    <div className="password-error">{passwordError}</div>
                  )}
                </div>

                <div className="form-options">
                  <div className="terms-agreement">
                    <input type="checkbox" id="terms" required />
                    <label htmlFor="terms">
                      I agree to the terms Terms of Service and Privacy Policy
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="email-password-register-btn"
                  disabled={emailPasswordLoading}
                >
                  {emailPasswordLoading
                    ? "Creating Account..."
                    : "Create Account"}
                </button>
              </form>

              <div className="features-section">
                <h3>Why join us?</h3>
                <div className="features-grid">
                  <div className="feature-item">
                    <i className="fas fa-shield-alt"></i>
                    <span>Secure & Safe</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-rocket"></i>
                    <span>Quick Setup</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-users"></i>
                    <span>Join Community</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-support"></i>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="register-footer">
              <p>
                Already have an account?{" "}
                <Link to="/login" state={location.state} className="signin-link">
                  Sign in
                </Link>
              </p>
              <div className="terms-section">
                {/* <p>By continuing, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a></p> */}
              </div>
            </div>
          </div>
        </div>
        <style jsx>{`
          .modern-register-page {
            min-height: 100vh;
            background-color: var(--bg-dark-primary);
            background-image: radial-gradient(circle at top left, rgba(99, 102, 241, 0.15), transparent 40%), radial-gradient(circle at bottom right, rgba(236, 72, 153, 0.1), transparent 40%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 60px 20px;
            position: relative;
            overflow-y: auto;
            overflow-x: hidden;
          }
          .register-container {
            position: relative;
            z-index: 1;
            width: 100%;
            max-width: 500px;
            margin: auto;
          }
          .register-card {
            background: var(--bg-dark-secondary);
            backdrop-filter: blur(16px);
            border-radius: var(--border-radius-xl);
            box-shadow: var(--shadow-lg);
            overflow: hidden;
            border: 1px solid var(--glass-border);
          }
          .register-header {
            padding: 40px 40px 30px;
            text-align: center;
            position: relative;
          }
          .close-button {
            position: absolute;
            top: 20px;
            right: 20px;
          }
          .close-link {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: var(--bg-dark-tertiary);
            border: 1px solid var(--glass-border);
            border-radius: 50%;
            color: var(--text-secondary);
            text-decoration: none;
            transition: all 0.3s ease;
          }
          .close-link:hover {
            background: rgba(255, 255, 255, 0.05);
            color: var(--text-primary);
            transform: scale(1.05);
          }
          .logo-section {
            margin-bottom: 30px;
          }
          .logo {
            height: 60px;
            width: auto;
          }
          .welcome-section h1 {
            font-size: 28px;
            font-weight: 700;
            color: var(--text-primary);
            margin-bottom: 10px;
          }
          .welcome-section p {
            font-size: 16px;
            color: var(--text-secondary);
            margin: 0;
          }
          .register-content {
            padding: 0 40px 30px;
          }
          .error-message {
            background: rgba(239, 68, 68, 0.1);
            border: 1px solid rgba(239, 68, 68, 0.2);
            border-radius: 12px;
            padding: 15px;
            margin-bottom: 25px;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .error-message i {
            color: var(--danger);
            font-size: 18px;
          }
          .error-message p {
            color: var(--danger);
            margin: 0;
            font-size: 14px;
          }

          /* Email/Password Form Styles */
          .email-password-form {
            margin-bottom: 25px;
          }
          .form-group {
            margin-bottom: 20px;
            text-align: left;
          }
          .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--text-primary);
            font-size: 14px;
          }
          .form-group input {
            width: 100%;
            padding: 14px 16px;
            border: 1px solid var(--glass-border);
            border-radius: 10px;
            font-size: 15px;
            transition: all 0.3s ease;
            background-color: var(--bg-dark-tertiary);
            color: var(--text-primary);
          }
          .form-group input::placeholder {
            color: var(--text-muted);
          }
          .form-group input:focus {
            outline: none;
            border-color: var(--accent-primary);
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.25);
            background-color: var(--bg-dark-secondary);
          }
          .password-error {
            color: var(--danger);
            font-size: 12px;
            margin-top: 5px;
          }
          .form-options {
            margin-bottom: 20px;
          }
          .terms-agreement {
            display: flex;
            align-items: flex-start;
            gap: 8px;
          }
          .terms-agreement input {
            width: 16px;
            height: 16px;
            accent-color: var(--accent-primary);
            margin-top: 2px;
          }
          .terms-agreement label {
            font-size: 14px;
            color: var(--text-secondary);
            cursor: pointer;
            line-height: 1.4;
          }
          .terms-agreement a {
            color: var(--accent-primary);
            text-decoration: none;
          }
          .terms-agreement a:hover {
            text-decoration: underline;
            color: var(--accent-primary-hover);
          }
          .email-password-register-btn {
            width: 100%;
            height: 56px;
            background: var(--accent-primary);
            border: none;
            border-radius: 12px;
            color: white;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: var(--shadow-glow);
          }
          .email-password-register-btn:hover {
            background: var(--accent-primary-hover);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
          }
          .email-password-register-btn:disabled {
            background: var(--bg-dark-tertiary);
            color: var(--text-muted);
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }

          .divider {
            display: flex;
            align-items: center;
            margin: 25px 0;
            color: var(--text-muted);
            font-size: 14px;
          }
          .divider::before,
          .divider::after {
            content: "";
            flex: 1;
            height: 1px;
            background: var(--glass-border);
          }
          .divider span {
            padding: 0 15px;
          }

          .google-signin-section {
            margin-bottom: 30px;
          }
          .google-signin-btn {
            width: 100%;
            height: 56px;
            background: var(--bg-dark-tertiary);
            border: 1px solid var(--glass-border);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            font-size: 16px;
            font-weight: 500;
            color: var(--text-primary);
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }
          .google-signin-btn:hover {
            border-color: var(--accent-primary);
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
            transform: translateY(-2px);
          }
          .google-signin-btn:active {
            transform: translateY(0);
          }
          .google-signin-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }
          .google-icon {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .features-section {
            text-align: center;
            margin-top: 30px;
          }
          .features-section h3 {
            font-size: 18px;
            font-weight: 600;
            color: var(--text-primary);
            margin-bottom: 20px;
          }
          .features-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          .feature-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px;
            background: rgba(99, 102, 241, 0.1);
            border: 1px solid rgba(99, 102, 241, 0.15);
            border-radius: 10px;
            font-size: 14px;
            color: var(--text-secondary);
            transition: all 0.2s;
          }
          .feature-item:hover {
            background: rgba(99, 102, 241, 0.15);
            transform: translateY(-2px);
          }
          .feature-item i {
            color: var(--accent-primary);
            font-size: 16px;
          }
          .register-footer {
            padding: 30px 40px;
            background: rgba(0, 0, 0, 0.2);
            text-align: center;
            border-top: 1px solid var(--glass-border);
          }
          .register-footer p {
            margin: 0 0 15px;
            color: var(--text-secondary);
            font-size: 14px;
          }
          .signin-link {
            color: var(--accent-primary);
            text-decoration: none;
            font-weight: 500;
          }
          .signin-link:hover {
            text-decoration: underline;
            color: var(--accent-primary-hover);
          }
          .terms-section p {
            font-size: 12px;
            color: var(--text-muted);
            margin: 0;
          }
          .terms-section a {
            color: var(--accent-primary);
            text-decoration: none;
          }
          .terms-section a:hover {
            text-decoration: underline;
          }
          @media (max-width: 768px) {
            .modern-register-page {
              padding: 100px 10px 40px 10px;
            }
            .register-header {
              padding: 30px 30px 20px;
            }
            .register-content {
              padding: 0 30px 20px;
            }
            .register-footer {
              padding: 20px 30px;
            }
            .welcome-section h1 {
              font-size: 24px;
            }
            .features-grid {
              grid-template-columns: 1fr;
            }
            .feature-item {
              justify-content: center;
            }
          }
          @media (max-width: 480px) {
            .register-header {
              padding: 20px 20px 15px;
            }
            .register-content {
              padding: 0 20px 15px;
            }
            .register-footer {
              padding: 15px 20px;
            }
            .welcome-section h1 {
              font-size: 22px;
            }
            .welcome-section p {
              font-size: 14px;
            }
            .google-signin-btn {
              height: 50px;
              font-size: 14px;
            }
            .email-password-register-btn {
              height: 50px;
              font-size: 15px;
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default Register;
